import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Bell, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], count: 0, danger_count: 0, warning_count: 0 });
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  };

  const handleClick = (notif) => {
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const severityConfig = {
    danger: { icon: AlertCircle, color: 'text-[#ff3b30]', bg: 'bg-[#ff3b30]/10', border: 'border-[#ff3b30]/20' },
    warning: { icon: AlertTriangle, color: 'text-[#ff9500]', bg: 'bg-[#ff9500]/10', border: 'border-[#ff9500]/20' },
    info: { icon: Info, color: 'text-[#007aff]', bg: 'bg-[#007aff]/10', border: 'border-[#007aff]/20' }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors"
        data-testid="notifications-bell-button"
      >
        <Bell className="w-5 h-5 text-zinc-300" />
        {data.count > 0 && (
          <span 
            className={`absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full text-xs font-bold flex items-center justify-center ${
              data.danger_count > 0 ? 'bg-[#ff3b30]' : 'bg-[#ff9500]'
            } text-white`}
            data-testid="notifications-count-badge"
          >
            {data.count > 9 ? '9+' : data.count}
          </span>
        )}
      </button>

      {open && (
        <div 
          className="absolute top-full left-0 mt-2 w-80 sm:w-96 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl shadow-black/50 z-50 backdrop-blur-md max-h-[500px] overflow-hidden flex flex-col"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between p-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-zinc-300" />
              <h3 className="text-white font-bold">التنبيهات</h3>
              {data.count > 0 && (
                <span className="text-xs text-zinc-400">({data.count})</span>
              )}
            </div>
            <button 
              onClick={() => setOpen(false)} 
              className="text-zinc-400 hover:text-white"
              data-testid="close-notifications-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {data.notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">لا توجد تنبيهات حالياً</p>
                <p className="text-zinc-600 text-xs mt-1">كل شيء على ما يرام ✨</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {data.notifications.map((notif) => {
                  const config = severityConfig[notif.severity] || severityConfig.info;
                  const Icon = config.icon;
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleClick(notif)}
                      className="w-full p-3 hover:bg-zinc-800/50 text-right transition-colors flex items-start gap-3"
                      data-testid="notification-item"
                    >
                      <div className={`p-2 rounded-lg ${config.bg} ${config.border} border flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notif.title}</p>
                        <p className="text-xs text-zinc-400 mt-1 truncate">{notif.message}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {data.notifications.length > 0 && (
            <div className="p-2 border-t border-zinc-800 bg-zinc-800/30">
              <p className="text-xs text-zinc-500 text-center">
                يتم التحديث كل دقيقة تلقائياً
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
